import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadSingleFile } from "../../services/FileDownloader";

const buttonStyle = {
  fontSize: 18,
  padding: "6px 12px",
  borderRadius: "8px",
  backgroundColor: "rgba(200, 200, 200, 0.25)",
  color: "white",
  border: "none",
  cursor: "pointer",
};

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoUrl: string;
  currentTime: number;
  onPause?: () => void;
  onPlay?: () => void;
  onEnded?: () => void;
  onLoadedMetadata?: () => void;
  onTimeUpdate?: () => void;
  isPlaying: boolean
}

const DraggableVideoWrapper: React.FC<Props> = ({ videoRef, videoUrl, currentTime, isPlaying, onPause, onPlay, onEnded, onLoadedMetadata, onTimeUpdate }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [zoom, setZoom] = useState(.4);

  useEffect(() => {
    const handleMouseUp = () => setDragging(false);
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  useEffect(() => {
    if (videoRef.current && !isPlaying) {
      videoRef.current.currentTime = currentTime;
    }
  }, [currentTime, videoRef]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 0.4), 5));
  };

  const resetView = () => {
    setZoom(.4);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <Box
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onWheel={handleWheel}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        cursor: dragging ? "grabbing" : "grab",
      }}
    >
      <Box
        sx={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transition: dragging ? "none" : "transform 0.2s ease-out",
          maxWidth: "none",
          maxHeight: "none",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <video
          ref={videoRef}
          src={videoUrl}
          crossOrigin="anonymous"
          controls={false}
          style={{
            display: "block",
            maxWidth: "none",
            maxHeight: "none",
            pointerEvents: "none",
            userSelect: "none",
          }}
          onPause={onPause}
          onPlay={onPlay}
          onEnded={onEnded}
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={onTimeUpdate}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          width: "100%",
          gap: 1,
          zIndex: 1,
        }}
      >
        <button onClick={() => setZoom((z) => Math.max(z - 0.1, 0.25))} style={buttonStyle}>-</button>
        <button onClick={() => downloadSingleFile(videoUrl)} style={buttonStyle}>
          <DownloadIcon />
        </button>
        <button onClick={resetView} style={buttonStyle}><RestartAltIcon /></button>
        <button onClick={() => setZoom((z) => Math.min(z + 0.1, 5))} style={buttonStyle}>+</button>
      </Box>
    </Box>
  );
};

export default DraggableVideoWrapper;
